import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { enumEntrySummaryState, enumEntrySummarySubmissionType } from '@data-layer/enums/entry-summaries';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { EntrySummaryService } from '@services/entry-summary.service';
import { BillItemPendingModel } from '@data-layer/models/payments';
import { ValidationMessageModel } from '@data-layer/models/common';
import { enumValidationMessageSeverity } from '@data-layer/enums/common';
import { ManagementEntrySummaryModel } from '@data-layer/models/management';
import { enumPaymentState } from '@data-layer/enums/payments';

export class EntrySummaryActionsHelper {
  private static canEditStatusesManual: enumEntrySummaryState[] = [
    enumEntrySummaryState.NOT_SENT,
    enumEntrySummaryState.NOT_VALID_PROVIDER,
    enumEntrySummaryState.DUTY_CALCULATION_COMPLETE,
    enumEntrySummaryState.REJECTED,
    enumEntrySummaryState.APPROVED,
    enumEntrySummaryState.NO_BILL_ON_FILE,
  ];
  private static canEditStatusesBroker: enumEntrySummaryState[] = [enumEntrySummaryState.NOT_SENT];

  public static isCanEdit(es: EntrySummaryModel): boolean {
    return (
      es &&
      (es.submissionType === enumEntrySummarySubmissionType.MANUAL
        ? this.canEditStatusesManual.includes(es.state)
        : this.canEditStatusesBroker.includes(es.state)) &&
      !es.releaseDate
    );
  }

  public static buildBrokerEntryActions(
    es: EntrySummaryModel | ManagementEntrySummaryModel,
    validationMessages: ValidationMessageModel[],
    pendingBills: BillItemPendingModel[],
  ): {
    validate: boolean;
    sent: boolean;
    paypal: boolean;
    approvePayments: boolean;
    confirmPayment: boolean;
  } {
    // const valid: boolean = validationMessages.length === 0;
    const validWithoutWarnings: boolean =
      validationMessages.filter((message) => message.severity !== enumValidationMessageSeverity.WARNING).length === 0;
    const actions = {
      validate: false,
      sent: false,
      paypal: false,
      approvePayments: false,
      confirmPayment: false,
    };
    switch (es.state) {
      case enumEntrySummaryState.NOT_SENT:
        if (validWithoutWarnings) {
          if (pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT)) {
            actions.paypal = true;
          } else {
            actions.sent = es.paymentState !== enumPaymentState.PENDING && es.dutiesPaymentState !== enumPaymentState.PENDING;
          }
        } else {
          actions.validate = true;
        }
        break;

      case enumEntrySummaryState.BROKER_PROCESSING_FAILED:
        if (pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT)) {
          actions.paypal = true;
        } else {
          actions.approvePayments = true;
        }
        break;

      case enumEntrySummaryState.BROKER_PROCESSING_FINISHED:
        if (pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT)) {
          actions.paypal = true;
        } else {
          actions.sent = false; // #9711
        }
        if (pendingBills.length && pendingBills.every((bill) => bill.paymentType === enumCompanyPaymentType.MONTHLY)) {
          actions.paypal = false;
          actions.confirmPayment = true;
        }
        break;

      case enumEntrySummaryState.BROKER_PROCESSING_FINISHING:
      case enumEntrySummaryState.BROKER_PROCESSING_PENDING:
      case enumEntrySummaryState.UNRECOVERABLE:
      case enumEntrySummaryState.DELETED:
      case enumEntrySummaryState.CBP_DELETE_PENDING:
      case enumEntrySummaryState.CBP_DELETED:
        break;

      case enumEntrySummaryState.AWAITING_ARRIVAL:
      case enumEntrySummaryState.BROKER_PROCESSING_CARGO_RELEASE_PENDING:
      case enumEntrySummaryState.ACCEPTED:
      case enumEntrySummaryState.APPROVED:
        if (pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT)) {
          actions.paypal = true;
        }
        if (pendingBills.length && pendingBills.every((bill) => bill.paymentType === enumCompanyPaymentType.MONTHLY)) {
          actions.approvePayments = true;
        }
        break;
    }

    return actions;
  }

  public static buildSelfEntryActions(
    es: EntrySummaryModel | ManagementEntrySummaryModel,
    validationMessages: ValidationMessageModel[],
    pendingBills: BillItemPendingModel[],
  ): {
    validate: boolean;
    sent: boolean;
    paypal: boolean;
    calculate: boolean;
    confirmPayment: boolean;
  } {
    const valid: boolean = validationMessages.length === 0;
    const validWithoutWarnings: boolean =
      validationMessages.filter((message) => message.severity !== enumValidationMessageSeverity.WARNING).length === 0;
    const actions = {
      validate: false,
      sent: false,
      paypal: false,
      calculate: false,
      confirmPayment: false,
    };
    switch (es.state) {
      case enumEntrySummaryState.NOT_SENT:
        if (valid) {
          actions.calculate = true;
        } else {
          actions.validate = true;
        }
        break;

      case enumEntrySummaryState.NOT_VALID_PROVIDER:
      case enumEntrySummaryState.APPROVED:
      case enumEntrySummaryState.NO_BILL_ON_FILE:
        if (valid) {
          actions.calculate = true;
        } else {
          actions.validate = true;
        }
        break;

      case enumEntrySummaryState.REJECTED:
        if (valid) {
          if (EntrySummaryService.modified(es)) {
            actions.calculate = true;
            break;
          }
          actions.sent = true;
        } else {
          actions.validate = true;
        }
        break;

      case enumEntrySummaryState.DUTY_CALCULATION_COMPLETE:
        if (!validWithoutWarnings) {
          actions.validate = true;
          break;
        }

        if (EntrySummaryService.modified(es)) {
          actions.calculate = true;
          break;
        }

        if (pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT)) {
          actions.paypal = true;
        } else {
          actions.sent = true;
        }

        break;

      case enumEntrySummaryState.DUTY_CALCULATION_PENDING:
      case enumEntrySummaryState.UNRECOVERABLE:
      case enumEntrySummaryState.BOND_PENDING:
      case enumEntrySummaryState.SENT_TO_PROVIDER:
      case enumEntrySummaryState.VALIDATED_AT_PROVIDER:
      case enumEntrySummaryState.PROVIDER_SEND_PENDING:
      case enumEntrySummaryState.SENT_TO_CBP:
      case enumEntrySummaryState.ACCEPTED:
      case enumEntrySummaryState.DELETED:
      case enumEntrySummaryState.CBP_DELETE_PENDING:
      case enumEntrySummaryState.CBP_DELETED:
        break;
    }

    return actions;
  }

  // public static isCanUploadDocuments(es: EntrySummary): boolean {
  //   return (
  //     this.isCanEdit(es) ||
  //     (es.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
  //     // es.state === enumEntrySummaryState.BROKER_PROCESSING_FAILED)
  //   );
  // }

  public static isCanRemoveDocuments(es: EntrySummaryModel | ManagementEntrySummaryModel): boolean {
    // this.isCanUploadDocument &&
    return [enumEntrySummaryState.NOT_SENT, enumEntrySummaryState.NOT_VALID_PROVIDER].includes(es.state);
  }
}
